// src/services/socket/SocketManager.js - WebSocket Connection Foundation
import { io } from 'socket.io-client';
import UniversalResponseHandler from './UniversalResponseHandler';
import useNotificationStore from '../../stores/NotificationStore';

class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.universalHandler = null;
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    
    // Event listeners storage
    this.eventListeners = new Map();
    
    // Connection state callbacks
    this.onConnectionStateChange = null;
    this.onAuthStateChange = null;
    
    // Debug mode (can be toggled via env)
    this.debug = import.meta.env.VITE_DEBUG_SOCKET === 'true';
    
    // Visibility-based keep-alive tracking
    this.keepAliveInterval = null;
    this.tabHiddenAt = null; // Timestamp when tab was hidden, null if visible
    this.visibilityListener = null;
    this.maxHiddenKeepAlive = 30 * 60 * 1000; // 30 minutes: keep sending keep-alive while tab is hidden

    // Reconnection state
    this.reconnectTimer = null;
    this.reconnectNotificationId = null; // Track the notification ID for updates

    // Start visibility tracking when constructed
    this.setupVisibilityTracking();
    
    this.log('🔌 SocketManager initialized');
  }

  // ================================
  // VISIBILITY-BASED KEEP-ALIVE
  // ================================

  /**
   * Track tab visibility to decide when to send keep-alive.
   * - Tab visible: always send keep-alive
   * - Tab hidden: send keep-alive for up to 30 minutes, then stop
   */
  setupVisibilityTracking() {
    this.visibilityListener = () => {
      if (document.hidden) {
        this.tabHiddenAt = Date.now();
        this.log('👁️ Tab hidden');
      } else {
        this.tabHiddenAt = null;
        this.log('👁️ Tab visible');
        // Send an immediate keep-alive when tab becomes visible again
        if (this.isAuthenticated) {
          this.sendKeepAlive();
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);
    this.log('👁️ Visibility tracking initialized');
  }

  /**
   * Start sending keep-alive signals when connected
   */
  startKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }

    this.keepAliveInterval = setInterval(() => {
      this.sendKeepAliveIfAppropriate();
    }, 180000); // Every 3 minutes

    this.log('💓 Keep-alive heartbeat started (3 minute interval)');
  }

  /**
   * Send keep-alive based on tab visibility
   */
  sendKeepAliveIfAppropriate() {
    if (!this.isAuthenticated) return;

    // Tab is visible — always send
    if (!this.tabHiddenAt) {
      this.sendKeepAlive();
      return;
    }

    // Tab is hidden — send only if hidden less than 30 minutes
    const hiddenDuration = Date.now() - this.tabHiddenAt;
    if (hiddenDuration < this.maxHiddenKeepAlive) {
      this.sendKeepAlive();
      this.log('💓 Keep-alive sent (tab hidden but within 30min window)');
    } else {
      this.log('😴 Keep-alive skipped (tab hidden > 30 minutes)');
    }
  }

  /**
   * Send a keep-alive event to the server
   */
  sendKeepAlive() {
    this.emit('keep_alive', { timestamp: Date.now() });
    this.log('💓 Keep-alive sent');
  }

  /**
   * Stop keep-alive interval
   */
  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
      this.log('💓 Keep-alive stopped');
    }
  }

  /**
   * Cleanup visibility listener and keep-alive
   */
  cleanupVisibilityTracking() {
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    this.stopKeepAlive();
    this.log('👁️ Visibility tracking cleaned up');
  }

  // ================================
  // CONNECTION MANAGEMENT
  // ================================

  /**
   * Connect to WebSocket server with JWT authentication
   * @param {string} token - JWT access token
   * @returns {Promise<boolean>} - Success/failure
   */
  async connect(token) {
    if (!token) {
      this.log('❌ No token provided for WebSocket connection');
      return false;
    }

    if (this.isConnecting) {
      this.log('⏳ Connection already in progress');
      return false;
    }

    if (this.isConnected && this.isAuthenticated) {
      this.log('✅ Already connected and authenticated');
      return true;
    }

    try {
      this.isConnecting = true;
      this.updateConnectionState('connecting');
      
      this.log('🔌 Connecting to WebSocket server...');
      
      // Create socket connection with auth
      const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5001';
      
      this.socket = io(socketUrl, {
        auth: {
          token: token
        },
        transports: ['websocket', 'polling'], // Fallback to polling if needed
        timeout: 10000, // 10 second connection timeout
        reconnection: false // We'll handle reconnection manually
      });

      this.universalHandler = new UniversalResponseHandler(this.socket);

      // Set up connection event handlers
      this.setupConnectionEvents();

      // Wait for connection result
      const connected = await this.waitForConnection();
      
      if (connected) {
        this.isConnected = true;
        this.isAuthenticated = true;
        this.reconnectAttempts = 0;
        this.updateConnectionState('connected');
        this.updateAuthState(true);
        
        // START KEEP-ALIVE WHEN CONNECTED
        this.startKeepAlive();
        
        this.log('✅ WebSocket connected and authenticated');
        return true;
      } else {
        this.log('❌ WebSocket connection failed');
        this.cleanup();
        return false;
      }

    } catch (error) {
      this.log('💥 Connection error:', error);
      this.cleanup();
      return false;
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.log('👋 Disconnecting from WebSocket server');
    
    // Stop keep-alive first
    this.stopKeepAlive();
    
    // **CRITICAL FIX**: Set flags FIRST to prevent any new operations
    this.isConnected = false;
    this.isAuthenticated = false;
    this.isConnecting = false;
    
    // Update states immediately
    this.updateAuthState(false);
    this.updateConnectionState('disconnected');
    
    // Clean up the socket connection with proper sequencing
    if (this.socket) {
      // Remove all listeners BEFORE disconnecting
      this.socket.removeAllListeners();
      
      // Disconnect with force flag to prevent reconnection attempts
      this.socket.disconnect(true);
      
      // Nullify the socket reference
      this.socket = null;
    }
    
    // Clean up universal handler
    if (this.universalHandler) {
      this.universalHandler.cleanup();
      this.universalHandler = null;
    }
    
    // Clear event listeners tracking
    this.eventListeners.clear();
    
    this.log('✅ WebSocket disconnected cleanly');
  }

  /**
   * Reconnect with exponential backoff
   */
  async reconnect(token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log('❌ Max reconnection attempts reached');
      this.updateConnectionState('failed');
      return false;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    this.log(`🔄 Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    this.updateConnectionState('reconnecting');
    
    await this.sleep(delay);
    
    return this.connect(token);
  }

  /**
   * Auto-reconnect flow triggered by server-initiated disconnect.
   * Gets a fresh token from localStorage and retries with backoff.
   * Shows reconnecting notification via NotificationStore.
   */
  async attemptAutoReconnect() {
    // Show "Reconnecting..." notification
    this.showReconnectingNotification();

    const token = localStorage.getItem('access_token');
    if (!token) {
      this.log('❌ No token available for auto-reconnect');
      this.showSessionExpiredNotification();
      return;
    }

    this.reconnectAttempts = 0;
    let success = false;

    while (this.reconnectAttempts < this.maxReconnectAttempts && !success) {
      this.reconnectAttempts++;
      const delay = Math.min(
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
        this.maxReconnectDelay
      );

      this.log(`🔄 Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
      this.updateConnectionState('reconnecting');

      await this.sleep(delay);

      // Abort if we were manually disconnected during the wait
      if (this.socket && this.isConnected) {
        this.log('✅ Already reconnected, aborting retry loop');
        success = true;
        break;
      }

      success = await this.connect(token);
    }

    if (success) {
      this.showReconnectedNotification();
    } else {
      this.log('❌ All reconnection attempts failed');
      this.updateConnectionState('failed');
      this.showReconnectFailedNotification();
    }
  }

  // ================================
  // RECONNECTION NOTIFICATIONS
  // ================================

  /**
   * Get the NotificationStore's state for imperative use
   */
  getNotificationStore() {
    return useNotificationStore;
  }

  showReconnectingNotification() {
    const store = this.getNotificationStore();
    if (!store) return;

    // Remove any previous reconnection notification
    if (this.reconnectNotificationId) {
      store.getState().remove(this.reconnectNotificationId);
    }

    this.reconnectNotificationId = store.getState().add({
      type: 'warning',
      message: 'Connection lost. Reconnecting...',
      duration: null, // persistent until we update it
      priority: 'high',
    });
  }

  showReconnectedNotification() {
    const store = this.getNotificationStore();
    if (!store) return;

    if (this.reconnectNotificationId) {
      store.getState().update(this.reconnectNotificationId, {
        type: 'success',
        message: 'Reconnected successfully',
        duration: 3000,
      });
      // Auto-dismiss after duration
      setTimeout(() => {
        store.getState().remove(this.reconnectNotificationId);
        this.reconnectNotificationId = null;
      }, 3000);
    }
  }

  showReconnectFailedNotification() {
    const store = this.getNotificationStore();
    if (!store) return;

    if (this.reconnectNotificationId) {
      store.getState().update(this.reconnectNotificationId, {
        type: 'error',
        message: 'Unable to reconnect. Please refresh the page.',
        duration: null, // stays until dismissed
      });
    }
  }

  showSessionExpiredNotification() {
    // This is a no-op here — the session expired modal is handled
    // by AuthContext when the token refresh fails
    this.log('🔒 No token for reconnect — session likely expired');
  }

  // ================================
  // EVENT COMMUNICATION
  // ================================

  /**
   * Emit an event to the server
   * @param {string} event - Event name
   * @param {object} data - Event data
   * @returns {boolean} - Success/failure
   */
  emit(event, data = {}) {
    if (!this.isAuthenticated) {
      this.log('❌ Cannot emit event - not authenticated:', event);
      return false;
    }

    this.log('📤 Emitting event:', event, data);
    this.socket.emit(event, data);
    return true;
  }

  /**
   * Listen for an event from the server
   * @param {string} event - Event name
   * @param {function} callback - Event handler
   */
  on(event, callback) {
    if (!this.universalHandler) {
      this.log('❌ Cannot listen for event - no universal handler:', event);
      return;
    }

    this.log('👂 Listening for universal response event:', event);
    this.universalHandler.on(event, callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {function} callback - Event handler to remove
   */
  off(event, callback) {
    if (!this.universalHandler) return;
    this.universalHandler.off(event, callback);
    this.log('🔇 Removed universal response listener for event:', event);
  }

  // ================================
  // CONNECTION STATE MANAGEMENT
  // ================================

  /**
   * Set up socket connection event handlers
   */
  setupConnectionEvents() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.log('🎉 Socket connected');
    });

    this.socket.on('disconnect', (reason) => {
      this.log('💔 Socket disconnected:', reason);
      this.isConnected = false;
      this.isAuthenticated = false;
      this.stopKeepAlive();
      this.updateConnectionState('disconnected');
      this.updateAuthState(false);

      // Notify stores to clear in-flight saving flags
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('socket-disconnected'));
      }

      // Auto-reconnect for server-initiated disconnects
      if (reason !== 'io client disconnect') {
        this.log('🔄 Attempting automatic reconnection...');
        this.attemptAutoReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      this.log('❌ Connection error:', error);
    });

    // Handle server errors
    this.socket.on('error', (error) => {
      this.log('💥 Socket error:', error);
    });
  }

  /**
   * Wait for connection to be established
   * @returns {Promise<boolean>}
   */
  waitForConnection() {
    return new Promise((resolve) => {
      if (!this.socket || !this.universalHandler) {
        resolve(false);
        return;
      }

      const timeout = setTimeout(() => {
        resolve(false);
      }, 10000);

      // Use universalHandler to listen for connection_established
      this.universalHandler.on('connection_established', (data) => {
        clearTimeout(timeout);
        this.log('✅ Authentication successful via universal response:', data);
        resolve(true);
      });

      this.socket.once('connect_error', () => {
        clearTimeout(timeout);
        resolve(false);
      });

      this.socket.once('authentication_failed', () => {
        clearTimeout(timeout);
        resolve(false);
      });
    });
  }

  /**
   * Clean up socket connection and event listeners
   */
  cleanup() {
    this.isConnected = false;
    this.isConnecting = false;
    
    // Clean up visibility tracking
    this.cleanupVisibilityTracking();
    
    if (this.universalHandler) {
      this.universalHandler.cleanup();
      this.universalHandler = null;
    }
    
    if (this.socket) {
      // Remove all custom event listeners
      this.eventListeners.forEach((callbacks, event) => {
        callbacks.forEach(callback => {
          this.socket.off(event, callback);
        });
      });
      this.eventListeners.clear();
      
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.updateConnectionState('disconnected');
  }

  // ================================
  // STATE CALLBACKS
  // ================================

  /**
   * Set callback for connection state changes
   * @param {function} callback - (state) => void
   */
  setConnectionStateCallback(callback) {
    this.onConnectionStateChange = callback;
  }

  /**
   * Set callback for auth state changes
   * @param {function} callback - (isAuthenticated) => void
   */
  setAuthStateCallback(callback) {
    this.onAuthStateChange = callback;
  }

  updateConnectionState(state) {
    this.log('🔄 Connection state:', state);
    if (this.onConnectionStateChange) {
      this.onConnectionStateChange(state);
    }
  }

  updateAuthState(isAuthenticated) {
    this.log('🔐 Auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
    if (this.onAuthStateChange) {
      this.onAuthStateChange(isAuthenticated);
    }
  }

  // ================================
  // UTILITY METHODS
  // ================================

  /**
   * Get current connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isAuthenticated: this.isAuthenticated,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  /**
   * Sleep utility for reconnection delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Debug logging
   */
  log(message, ...args) {
    if (this.debug) {
      console.log(`[SocketManager] ${message}`, ...args);
    }
  }
}

// Create singleton instance
const socketManager = new SocketManager();

export default socketManager;